import { Box, Typography, Stack, alpha, LinearProgress } from '@mui/material';
import { CheckCircle, RadioButtonUnchecked, FiberManualRecord } from '@mui/icons-material';
import { DispatchRow, formatStatus, STATUS_BY_TAB } from './dispatchTypes';
import { DISPATCH } from './dispatchTheme';

const TRANSPORT_STEPS = ['pending', 'searching', 'bidding', 'accepted', 'arrived', 'started', 'completed'];
const SERVICE_STEPS = ['pending', 'confirmed', 'assigned', 'in_progress', 'completed'];

function stepsForService(service: DispatchRow['service']) {
  if (service === 'transportation') return TRANSPORT_STEPS;
  return SERVICE_STEPS;
}

interface DispatchTripProgressProps {
  row: DispatchRow;
  compact?: boolean;
}

export default function DispatchTripProgress({ row, compact }: DispatchTripProgressProps) {
  const steps = stepsForService(row.service);
  const current = row.status.toLowerCase();
  const isCancelled = ['cancelled', 'refunded'].includes(current);
  const isLive = STATUS_BY_TAB.active.includes(current);
  const currentIdx = steps.findIndex((s) => s === current);
  const progress = currentIdx >= 0 ? Math.round(((currentIdx + 1) / steps.length) * 100) : 0;

  return (
    <Box sx={{ mb: compact ? 0 : 2, minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1} minWidth={0}>
        <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing="0.06em" noWrap>
          Trip Progress
        </Typography>
        {isLive && (
          <Stack direction="row" alignItems="center" spacing={0.5} flexShrink={0}>
            <FiberManualRecord sx={{ fontSize: 10, color: DISPATCH.success }} />
            <Typography variant="caption" fontWeight={800} color="success.main">Live</Typography>
          </Stack>
        )}
      </Stack>

      <LinearProgress
        variant="determinate"
        value={isCancelled ? 0 : progress}
        sx={{
          mb: compact ? 1 : 1.5,
          height: 6,
          borderRadius: 3,
          bgcolor: alpha(isCancelled ? DISPATCH.danger : DISPATCH.primary, 0.12),
          '& .MuiLinearProgress-bar': {
            borderRadius: 3,
            bgcolor: isCancelled ? DISPATCH.danger : isLive ? DISPATCH.success : DISPATCH.primary,
          },
        }}
      />

      {compact ? (
        <Stack spacing={0.5} minWidth={0}>
          <Typography variant="body2" fontWeight={800} color={isLive ? 'success.main' : 'text.primary'}>
            {formatStatus(current)} · {progress}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Step {Math.max(currentIdx + 1, 1)} of {steps.length}
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={0.75} minWidth={0}>
          {steps.map((step, stepIdx) => {
            const done = !isCancelled && currentIdx > stepIdx;
            const active = step === current;
            return (
              <Stack key={step} direction="row" alignItems="center" spacing={1} minWidth={0}>
                {done ? (
                  <CheckCircle sx={{ fontSize: 18, color: DISPATCH.success, flexShrink: 0 }} />
                ) : active ? (
                  <FiberManualRecord sx={{ fontSize: 18, color: isLive ? DISPATCH.success : DISPATCH.primary, flexShrink: 0 }} />
                ) : (
                  <RadioButtonUnchecked sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }} />
                )}
                <Typography
                  variant="caption"
                  fontWeight={active ? 800 : 500}
                  color={active ? 'text.primary' : done ? 'success.main' : 'text.secondary'}
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {formatStatus(step)}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      )}

      {isCancelled && (
        <Typography variant="caption" color="error.main" fontWeight={700} display="block" mt={0.5}>
          Trip cancelled
        </Typography>
      )}
    </Box>
  );
}
